import { formatForDisplay, type Hotkey } from '@tanstack/react-hotkeys';

/**
 * The palette's *frame* bindings — the ones that aren't a command. Commands
 * declare their own `hotkey`/`sequence` in `commands/registry.tsx`; everything
 * here is behaviour of the shell itself (list navigation, opening a hit, going
 * back), owned by whichever component can see the context.
 *
 * `Mod` is ⌘ on mac and Ctrl elsewhere. Never hand-write display glyphs (⌘, ↵,
 * ⇥) — `displayKeys` derives them from these strings.
 */
export const SPINE = {
  togglePalette: 'Mod+K',
  actionsMenu: 'Mod+Shift+K',
  /** Carries what's typed on the root into the search page. */
  tabToSearch: 'Tab',
  back: 'Escape',
  /** Steps back a page when the input is empty. */
  backFromEmptyInput: 'Backspace',
  browseUp: 'ArrowUp',
  browseDown: 'ArrowDown',
  open: 'Enter',
  openInNewTab: 'Mod+Enter',
  /** Same chord as `openInNewTab`, on the filter page. */
  saveFilter: 'Mod+Enter',
} as const satisfies Record<string, Hotkey>;

/**
 * The keycaps for a hotkey, one token per `Kbd`: `Mod+Enter` → `['⌘', '↵']` on
 * mac, `['Ctrl', '↵']` elsewhere. Platform is passed in (not detected) so the
 * caps match `useIsMac`, which is what the components already render from.
 */
export function displayKeys(
  hotkey: Hotkey | (string & {}),
  isMac: boolean
): string[] {
  return formatForDisplay(hotkey, { platform: isMac ? 'mac' : 'windows' })
    .split(/[+\s]+/)
    .filter(Boolean);
}

export interface ShortcutRow {
  readonly requiresSearch?: boolean;
  readonly label: string;
  readonly hotkey?: Hotkey;
  readonly sequence?: readonly [Hotkey, Hotkey];
}

/**
 * The cheat sheet's "I söket" section: the frame bindings, which have no command
 * row to be listed from. The command sections are derived from the registry.
 */
export const SPINE_ROWS: readonly ShortcutRow[] = [
  { label: 'Öppna eller stäng paletten', hotkey: SPINE.togglePalette },
  { label: 'Bläddra nedåt i träffarna', hotkey: SPINE.browseDown },
  { label: 'Bläddra uppåt i träffarna', hotkey: SPINE.browseUp },
  { label: 'Öppna träffen', hotkey: SPINE.open },
  {
    label: 'Öppna träffen i ny flik',
    hotkey: SPINE.openInNewTab,
    requiresSearch: true,
  },
  {
    label: 'Snabbåtgärder för träffen',
    hotkey: SPINE.actionsMenu,
    requiresSearch: true,
  },
  {
    label: 'Ta med det du skrivit in i söket',
    hotkey: SPINE.tabToSearch,
    requiresSearch: true,
  },
  { label: 'Tillbaka en nivå', hotkey: SPINE.back },
  { label: 'Tillbaka från tomt sökfält', hotkey: SPINE.backFromEmptyInput },
];
